/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: FeatureItem.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-FEATURES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FeatureItem - Строка задачи с порядковым номером, статусом и действиями.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-28 - UC-020: статус BACKLOG в STATUS_DISPLAY.
 * LAST_CHANGE: 2026-07-14 - UX: номер в очереди, статус на русском, оценки, hover-строка.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракт компонента и парный END_METHOD.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createInteractiveSurfaceTransition } from '../../../theme/appTheme';

const STATUS_DISPLAY = {
  BACKLOG: { label: 'Бэклог', color: 'default' },
  IN_PROGRESS: { label: 'В работе', color: 'primary' },
  ARCHIVED: { label: 'В архиве', color: 'default' },
};

function formatStartDate(isoDate) {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return '';
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function sumEstimates(estimates) {
  if (!estimates?.length) return 0;
  return estimates.reduce((sum, item) => sum + (item.estimate ?? 0), 0);
}

/* START_METHOD_FeatureItem */
/* START_CONTRACT: FeatureItem
 * PURPOSE: Отобразить строку задачи со статусом, датой и действиями (drag/редакт./архив/удаление).
 * INPUTS: { feature: Feature, position: number, onEdit, onArchive, onDelete, sortable: boolean, isLast?: boolean }.
 * OUTPUTS: { JSX.Element - строка задачи }.
 * SIDE_EFFECTS: нет (делегирует колбэки, использует useSortable).
 * LINKS: M-FE-FEATURES
 * END_CONTRACT: FeatureItem
 */
export default function FeatureItem({
  feature,
  position,
  onEdit,
  onArchive,
  onDelete,
  sortable = false,
  isLast = false,
}) {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: feature.id, disabled: !sortable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const statusMeta = STATUS_DISPLAY[feature.status] ?? {
    label: feature.statusLabel,
    color: 'default',
  };
  const startDateLabel = formatStartDate(feature.startDate);
  const totalHours = sumEstimates(feature.estimates);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1.5,
        pl: sortable ? 0.75 : 2,
        pr: 2,
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
        opacity: feature.isArchived ? 0.72 : 1,
        bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
        boxShadow: isDragging ? `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}` : 'none',
        position: 'relative',
        zIndex: isDragging ? 2 : 'auto',
        borderRadius: isDragging ? 0.5 : 0,
        transition: createInteractiveSurfaceTransition(theme, ['background-color', 'box-shadow']),
        '&:hover': {
          bgcolor: feature.isArchived
            ? alpha(theme.palette.text.primary, 0.02)
            : alpha(theme.palette.primary.main, 0.03),
          '& .feature-row-actions': {
            opacity: 1,
          },
        },
        '@media (hover: none)': {
          '& .feature-row-actions': {
            opacity: 1,
          },
        },
        '@media (prefers-reduced-motion: reduce)': {
          boxShadow: 'none',
        },
      }}
    >
      {sortable && (
        <Tooltip title="Перетащите для изменения порядка">
          <Box
            component="button"
            type="button"
            aria-label="Изменить порядок"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              p: 0.25,
              minWidth: 0,
              border: 'none',
              borderRadius: 0.5,
              bgcolor: 'transparent',
              color: 'text.disabled',
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none',
              transition: createInteractiveSurfaceTransition(theme, ['color']),
              '&:hover': {
                color: 'text.secondary',
              },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 1,
              },
            }}
            {...attributes}
            {...listeners}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        </Tooltip>
      )}

      <Box
        aria-hidden
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 0.5,
          bgcolor: feature.isArchived
            ? alpha(theme.palette.text.primary, 0.06)
            : alpha(theme.palette.primary.main, 0.1),
          color: feature.isArchived ? 'text.disabled' : 'primary.main',
          fontFamily: '"Source Sans 3", monospace',
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '-0.02em',
        }}
      >
        {position}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{
              letterSpacing: '-0.01em',
              textDecoration: feature.isArchived ? 'line-through' : 'none',
              color: feature.isArchived ? 'text.secondary' : 'text.primary',
            }}
          >
            {feature.name}
          </Typography>
          <Chip
            label={statusMeta.label}
            size="small"
            color={statusMeta.color}
            variant={feature.isArchived ? 'outlined' : 'filled'}
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.35, flexWrap: 'wrap' }}>
          {startDateLabel && (
            <Typography variant="caption" color="text.secondary">
              Старт {startDateLabel}
            </Typography>
          )}
          {totalHours > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontFamily: '"Source Sans 3", monospace',
                fontWeight: 600,
              }}
            >
              {totalHours} ч
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        className="feature-row-actions"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0,
          opacity: { xs: 1, sm: 0.55 },
          transition: createInteractiveSurfaceTransition(theme, ['opacity']),
        }}
      >
        <Tooltip title="Редактировать">
          <IconButton
            size="small"
            aria-label={`Редактировать ${feature.name}`}
            onClick={() => onEdit(feature)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!feature.isArchived && (
          <Tooltip title="Архивировать">
            <IconButton
              size="small"
              aria-label={`Архивировать ${feature.name}`}
              onClick={() => onArchive(feature)}
            >
              <ArchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Удалить">
          <IconButton
            size="small"
            color="error"
            aria-label={`Удалить ${feature.name}`}
            onClick={() => onDelete(feature)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
/* END_METHOD_FeatureItem */
